// Pricing migration 32 (ADO-3985), T12: the product page's deal section, its own card between the
// hero and the Product Information card (frames d/05 and t/05; the confirmed container, heading
// and highlight).
//
// - One deal: the drawn heading "This multi-buy", the deal's name (the feed's description, as sent)
//   and its tiers in promotion red under it, R5's treatment (05: heading/h5 and heading/h6, the name
//   two lines then "…", whole on hover and for screen readers), then the deal's body as the dialog
//   draws it (the free product, the progress, the lines with their steppers), with no red header
//   and no Done. A deal with no description keeps the tier text as its name.
// - Several deals (R4 + R5 05): "Deals on this product (N)", one card for all of them. Each deal
//   is a row button with aria-expanded (its name, the mechanic in the offer red and its status)
//   opening in place; only one is open at a time. The deal the strip showed is open, and "See deal"
//   opens the strip's deal.
// - Below 768px (A3): the deal as the red-header panel with Done; several deals keep one panel,
//   with "Also on this product" swapping it (the dialog's R4 07o pattern, a held derivation).
// - The heading is an `<h2>` with `tabindex="-1"` so "See deal" can move focus to it; on a phone it
//   is visually hidden, the panel's header standing in for it.
// - Shown only with the flag on and a deal (D-5, AC6): nothing while the deal data is loading, has
//   failed or holds no deal.
import { useId } from 'react';
import type { MultibuyDeal } from '../../api/multibuy-deals.client';
import { useIsNarrowViewport } from '../../basket/useIsNarrowViewport';
import { Icon } from '../../design-system';
import { PHONE_MEDIA_QUERY, TABLET_DOWN_MEDIA_QUERY } from '../../design-system/breakpoints';
import { MultibuyDealView } from '../../multibuy/MultibuyDealView';
import { cardDeal, dealEndText, dealOfferText, tiersText } from '../../multibuy/multibuyDealText';
import { useMultibuyDealsEntry } from '../../multibuy/useMultibuyDealsStore';
import { realPricesEnabled } from '../../pricing/realPrices';
import type { DealSectionReveal } from './useDealSectionReveal';
import styles from './MultibuyDealSection.module.css';

export const DEAL_SECTION_HEADING = 'This multi-buy';

export interface MultibuyDealSectionProps {
  readonly sku: string;
  /** The page's reveal: the section and heading refs "See deal" scrolls and focuses, and the
   *  highlight it shows. */
  readonly target: Pick<DealSectionReveal, 'sectionRef' | 'headingRef' | 'highlight'>;
  /** The open deal's offer code ("See deal" names the strip's); `null` opens the one the strip
   *  shows. */
  readonly openOffer?: string | null;
  readonly onOpenOffer?: (offerCode: string) => void;
  /** Done on the phone's panel (A3). */
  readonly onDone?: () => void;
}

/** The deals the section lists, or `null` when it is hidden. */
export function useSectionDeals(sku: string): readonly MultibuyDeal[] | null {
  const entry = useMultibuyDealsEntry(sku);
  if (!realPricesEnabled() || entry?.state !== 'ready' || entry.deals.length === 0) return null;
  return entry.deals;
}

/** A collapsed or open row's status (R4 + R5 05): the picked count once the shop has started
 *  the deal, else its end date. */
function rowStatus(deal: MultibuyDeal): string | null {
  const { pooledQuantity, nextThreshold } = deal.progress;
  if (pooledQuantity > 0 && nextThreshold !== undefined && deal.kind === 'FREE_UNITS') {
    return `${pooledQuantity} of ${nextThreshold} picked`;
  }
  return dealEndText(deal);
}

function DealHead({ deal }: { readonly deal: MultibuyDeal }) {
  const tiers = deal.kind === 'FREE_UNITS' ? tiersText(deal) : null;
  const described = deal.description !== undefined && deal.description !== '';
  const name = described ? deal.description : (tiers ?? '');
  return (
    <div className={styles.dealHead} data-multibuy-deal-head="">
      <h3 className={styles.dealTitle} title={name}>
        {name}
      </h3>
      {described && tiers !== null ? (
        <p className={styles.tiers} data-part="deal-tiers">
          {tiers}
        </p>
      ) : null}
    </div>
  );
}

function DealRow({
  deal,
  open,
  touch,
  onOpen,
}: {
  readonly deal: MultibuyDeal;
  readonly open: boolean;
  readonly touch: boolean;
  readonly onOpen: () => void;
}) {
  const bodyId = useId();
  const mechanic = deal.kind === 'FREE_UNITS' ? dealOfferText(deal) : null;
  const name = deal.description ?? mechanic ?? '';
  const status = rowStatus(deal);
  return (
    <div className={styles.deal} data-multibuy-deal={deal.multibuyOfferCode}>
      <h3 className={styles.rowHeading}>
        <button
          type="button"
          className={styles.row}
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={onOpen}
        >
          <span className={styles.rowText}>
            <span className={styles.rowName} title={name}>
              {name}
            </span>
            {mechanic !== null && mechanic !== name ? (
              <span className={styles.rowMechanic}>{mechanic}</span>
            ) : null}
          </span>
          {status === null ? null : <span className={styles.rowStatus}>{status}</span>}
          <span className={styles.chevron} data-open={open ? '' : undefined} aria-hidden="true">
            <Icon name={open ? 'caretUp' : 'caretDown'} size={24} />
          </span>
        </button>
      </h3>
      <div id={bodyId} hidden={!open} className={styles.rowBody}>
        {open ? <MultibuyDealView deal={deal} layout="section" touch={touch} /> : null}
      </div>
    </div>
  );
}

export function MultibuyDealSection({
  sku,
  target,
  openOffer = null,
  onOpenOffer,
  onDone,
}: MultibuyDealSectionProps) {
  const headingId = useId();
  const deals = useSectionDeals(sku);
  const phone = useIsNarrowViewport(PHONE_MEDIA_QUERY);
  // Below 1024px the steppers take their Touch size (t/05: "Steppers use their Touch size").
  const touch = useIsNarrowViewport(TABLET_DOWN_MEDIA_QUERY);
  if (deals === null) return null;

  const open =
    deals.find((deal) => deal.multibuyOfferCode === openOffer) ?? (cardDeal(deals) as MultibuyDeal);
  const several = deals.length > 1;
  const heading = several ? `Deals on this product (${deals.length})` : DEAL_SECTION_HEADING;

  return (
    <section
      ref={target.sectionRef}
      className={styles.section}
      aria-labelledby={headingId}
      data-multibuy-section=""
      data-layout={phone ? 'panel' : 'card'}
      data-highlight={target.highlight ?? undefined}
    >
      <h2
        ref={target.headingRef}
        id={headingId}
        className={phone ? styles.visuallyHidden : styles.heading}
        tabIndex={-1}
      >
        {heading}
      </h2>
      {phone ? (
        <div data-multibuy-deal={open.multibuyOfferCode}>
          <MultibuyDealView
            deal={open}
            layout="panel"
            touch
            otherDeals={deals.filter((deal) => deal !== open)}
            onShowDeal={(code) => onOpenOffer?.(code)}
            {...(onDone === undefined ? {} : { onDone })}
          />
        </div>
      ) : several ? (
        <div className={styles.rows} data-multibuy-deals="">
          {deals.map((deal) => (
            <DealRow
              key={deal.multibuyOfferCode}
              deal={deal}
              open={deal === open}
              touch={touch}
              onOpen={() => onOpenOffer?.(deal.multibuyOfferCode)}
            />
          ))}
        </div>
      ) : (
        <div className={styles.deals} data-multibuy-deals="">
          <div className={styles.deal} data-multibuy-deal={open.multibuyOfferCode}>
            <DealHead deal={open} />
            <MultibuyDealView deal={open} layout="section" touch={touch} />
          </div>
        </div>
      )}
    </section>
  );
}
